<script lang="ts">
    import { Card, Button, Input, Select, Checkbox, Radio, Textarea, Helper } from "flowbite-svelte";
    import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
    import { faSave, faTimes, faAsterisk } from "@fortawesome/free-solid-svg-icons";
    import { createEventDispatcher } from "svelte";
    import type { ProjectData, Project, Lookup, ProjectInput } from "../../services/api/types.js";
    import MonthYearPicker from '../common/MonthYearPicker.svelte';
    import { getProjectsByCustomer } from "../../services/api/data-handler.js";
    import { logError, logInfo, logWarning } from "../../services/logging/log-handler.js";

    type ValidFields = string;

    interface LookupOption {
        id: string;
        name: string;
    }

    interface MetadataLayoutField {
        id: string;
        label: string;
        type: 'lookup' | 'items' | 'text' | 'textarea' | 'yearmonthpicker';
        required?: boolean;
        helper?: string;
        format?: string;
        openAbove?: boolean;
        items?: Array<{
            id: string;
            label: string;
            type: 'lookup' | 'text' | 'textarea' | 'yearmonthpicker';
            openAbove?: boolean;
            format?: string;
            helper?: string;
        }>;
    }

    const metadataLayout = {
        layout: [
            {
                id: "project_status",
                label: "Project Status",
                required: true,
                type: "lookup"
            },
            {
                id: "therapeutic_area",
                label: "Therapeutic Area",
                required: true,
                type: "lookup"
            },
            {
                id: "indication",
                label: "Indication",
                type: "text",
                required: true
            },
            {
                id: "project_source",
                label: "Project Source",
                type: "lookup",
                required: true
            },
            {
                id: "contacts",
                label: "Project Directors",
                type: "textarea",
                format: "comma-separated",
                helper: "Enter the names separated by commas."
            },
            {
                id: "finalized",
                label: "Finalized",
                type: "yearmonthpicker",
                required: true,
                openAbove: false
            },
            {
                id: "business_unit",
                label: "Business Unit",
                required: true,
                type: "lookup"
            },
            {
                id: "review_type",
                label: "Review Type",
                required: true,
                type: "lookup"
            },
            {
                id: "review_topics",
                label: "Review Topics",
                required: true,
                type: "lookup"
            },
            {
                id: "stakeholders",
                label: "Stakeholders",
                required: true,
                type: "lookup"
            },
            {
                id: "time_horizon",
                label: "Time Horizon",
                type: "items",
                required: false,
                items: [
                    {
                        id: "start",
                        label: "Start",
                        type: "yearmonthpicker",
                        openAbove: true
                    },
                    {
                        id: "end",
                        label: "End",
                        type: "yearmonthpicker",
                        openAbove: true
                    }
                ]
            }
        ] as MetadataLayoutField[]
    } as const;

    let projectInput: ProjectInput;
    let lookups: Record<string, Lookup>;
    let requiredFields: Set<string>;
    let errors: Record<string, string>;
    let nameDuplicate = false;
    let nameDebounceTimer: ReturnType<typeof setTimeout>;
    let allProjectsForCustomer: Project[] = [];

    export let object: ProjectData;
    export const action: "add" | "edit" = "add";
    
    // Only initialize projectInput when object is first available
    let initialized = false;
    $: if (object && !initialized) {
        initialized = true;
        
        // Build the list of required fields
        requiredFields = new Set(['name']);
        metadataLayout.layout.forEach(field => {
            if (field.required === true) {
                requiredFields.add(field.id);
            }
        });

        // Initialize errors object with all required fields
        errors = Array.from(requiredFields).reduce((acc, field) => {
            acc[field] = "";
            return acc;
        }, {} as Record<string, string>);
        
        projectInput = {
            project_id: object.project.project_id,
            name: object.project.name,
            title: object.project.title,
            customer_id: object.project.customer.customer_id,
            metadata: object.project.metadata,
            global_lookup_id: object.project.global_lookup_id,
            customer_lookup_id: object.project.customer_lookup_id || undefined
        };

        const entries = Object.entries(object.lookups);
        const sortedEntries = entries.sort(([, a], [, b]) => a.order - b.order);
        lookups = Object.fromEntries(sortedEntries);
        
        // Validate all fields immediately
        validateAllFields();
    }

    const dispatch = createEventDispatcher();

    $: errorState = errors as Record<string, string>;
    $: hasErrors = Object.values(errorState).some((error) => error !== "");

    $: getInputClass = (field: keyof typeof errors) => {
        return errorState[field] ? "error" : "";
    };

    function saveChanges() {
        projectInput.title = projectInput.title.trim(); 
        projectInput.name = projectInput.name.trim();
        validateAllFields();
        if (Object.values(errorState).every((error) => error === "")) {
            dispatch("save", projectInput);
        }
    }

    function cancelEdit() {
        dispatch("close");
    }
    
    function handleCheckboxChange(e: Event, field: string, option: LookupOption) {
        const target = e.target as HTMLInputElement;
        const current = projectInput.metadata[field] as LookupOption[] || [];
        const newValue = target.checked 
            ? [...current, option]
            : current.filter((item: LookupOption) => item.id !== option.id);
        
        projectInput.metadata[field] = newValue;
        // Always validate after changing, including when array becomes empty
        validateFieldSafe(field, newValue);
    }

    function validateAllFields() {
        if (!projectInput || !lookups) return; // Guard against undefined state
        
        // Validate all required fields
        requiredFields.forEach(field => {
            if (field === 'name') {
                validateField(field, projectInput.name);
            } else {
                validateFieldSafe(field, projectInput.metadata[field]);
            }
        });
    }

    function validateField(field: ValidFields, value: any) {
        const metadataField = metadataLayout.layout.find(f => f.id === field);
        
        if (field === "name") {
            if (!value?.trim()) {
                errors[field] = "Name is required";
            } else if (nameDuplicate) {
                errors[field] = "Project name must be unique for the customer";
            } else {
                errors[field] = "";
            }
        } else if (metadataField?.required) {
            switch (metadataField.type) {
                case 'items':
                    // Check if all items have both year and month
                    const hasAllItemFields = metadataField.items?.every(item => 
                        value?.[item.id]?.year !== undefined && 
                        value?.[item.id]?.month !== undefined
                    );
                    errors[field] = !hasAllItemFields ? `${metadataField.label} is required` : "";
                    break;
                case 'lookup':
                    if (Array.isArray(value)) {
                        // For multi-select, check if array has at least one item
                        errors[field] = value.length === 0 ? `${metadataField.label} is required` : "";
                    } else {
                        // For single-select
                        errors[field] = !value ? `${metadataField.label} is required` : "";
                    }
                    break;
                case 'text':
                case 'textarea':
                    if (!value || typeof value !== 'string' || !value.trim()) {
                        errors[field] = `${metadataField.label} is required`;
                    } else {
                        errors[field] = "";
                    }
                    break;
                case 'yearmonthpicker':
                    if (!value?.year || !value?.month) {
                        errors[field] = `${metadataField.label} is required`;
                    } else {
                        errors[field] = "";
                    }
                    break;
                default:
                    errors[field] = "";
            }
        } else {
            errors[field] = "";
        }
        errorState = { ...errors };
    }

    function validateFieldSafe(field: string, value: any) {
        validateField(field as ValidFields, value);
    }

    function isOptionSelected(field: string, optionId: string): boolean {
        const items = projectInput.metadata[field] as LookupOption[] | undefined;
        return items?.some((item: LookupOption) => item.id === optionId) || false;
    }

    async function debouncedNameChange(value: string) {
        clearTimeout(nameDebounceTimer);
        nameDuplicate = false;
        validateField('name', value);
        
        const trimmedValue = value?.trim();
        if (!trimmedValue || !projectInput.customer_id) {
            return;
        }

        nameDebounceTimer = setTimeout(async () => {
            try {
                if (allProjectsForCustomer.length === 0 || 
                    allProjectsForCustomer[0]?.customer.customer_id !== projectInput.customer_id) {
                    const projectsData = await getProjectsByCustomer(projectInput.customer_id);
                    allProjectsForCustomer = projectsData.projects || [];
                }

                const isDuplicate = allProjectsForCustomer.some(
                    (project) =>
                        project.project_id !== projectInput.project_id &&
                        project.name.toLowerCase().trim() === trimmedValue.toLowerCase()
                );

                nameDuplicate = isDuplicate;
                validateField('name', trimmedValue);
            } catch (error) {
                logError(error, "ProjectObject", undefined, { message: "Error checking project name uniqueness" });
            }
        }, 500);
    }

    function handleNameChange(event: Event) {
        const target = event.target as HTMLInputElement;
        projectInput.name = target.value;
        debouncedNameChange(target.value);
    }

    function handleTitleChange(event: Event) {
        const target = event.target as HTMLTextAreaElement;
        projectInput.title = target.value;
        validateField('title', target.value);
    }

    function handleTextInput(e: Event, field: string) {
        const target = e.target as HTMLTextAreaElement;
        const metadataField = metadataLayout.layout.find(f => f.id === field);
        const isBlurEvent = e.type === 'blur';
        
        if (isBlurEvent || (e as KeyboardEvent).key === 'Enter') {
            if (metadataField?.format === 'comma-separated') {
                const values = target.value
                    .split(',')
                    .map(v => v.trim())
                    .filter(v => v !== '');
                    
                // Update the display value with properly formatted text
                target.value = values.join(', ');
                projectInput.metadata[field] = values;
            } else {
                // Handle as regular textarea input
                projectInput.metadata[field] = target.value;
            }
            validateFieldSafe(field, projectInput.metadata[field]);
        }
    }

    function handleInputValue(e: Event): string {
        const target = e.target as HTMLInputElement;
        return target.value;
    }
</script>

<Card class="app-mutation-area max-w-lg flex flex-col">
    <div class="space-y-2">
        <!-- Basic fields -->
        <div class="pl-1 pr-2">
            <h4 class="card-label-text mt-1">Name 
                {#if errorState['name']} 
                    <FontAwesomeIcon icon={faAsterisk} title="Required field" class="text-red-400" />
                {/if}
            </h4>
            {#if nameDuplicate}
                <Helper class="helper-text error mb-1">Project name must be unique for the customer</Helper>
            {:else}
                <Helper class="helper-text mb-1">Enter a unique project name for the customer</Helper>
            {/if}
            <Input 
                class="app-field {getInputClass('name')}" 
                type="text" 
                value={projectInput.name}
                on:input={handleNameChange} 
            />
        </div>

        <!-- Scrollable area starts -->
        <div class="overflow-y-auto flex-grow pr-2 pl-1" style="height: calc(100vh - 18.5rem);">
            <div class="space-y-2">
                <div>
                    <h4 class="card-label-text mt-1">Title 
                        {#if errorState['title']} 
                            <FontAwesomeIcon icon={faAsterisk} title="Required field" class="text-red-400" />
                        {/if}
                    </h4> 
                    <Textarea 
                        class="app-field {getInputClass('title')} h-18" 
                        value={projectInput.title}
                        rows={4}
                        on:input={handleTitleChange} 
                    />
                </div>

                <div class="space-y-2">
                    {#each metadataLayout.layout as field}
                        <div>
                            <h4 class="card-label-text mt-2 mb-1">{field.label}
                                {#if field.required && errorState[field.id]}
                                    <FontAwesomeIcon icon={faAsterisk} title="Required field" class="text-red-400" />
                                {/if}
                            </h4>
                            {#if field.helper}
                                <Helper class="helper-text mb-1">{field.helper}</Helper>
                            {/if}

                            {#if field.type === 'lookup'}
                                {#if lookups[field.id]?.type === 'multi-select'}
                                    <div class="ml-4 space-y-1">
                                        {#each lookups[field.id].list as option}
                                            <Checkbox
                                                class="card-checkbox"
                                                checked={isOptionSelected(field.id, option.id)}
                                                on:change={(e) => handleCheckboxChange(e, field.id, option)}
                                            >
                                                {option.name}
                                            </Checkbox>
                                        {/each}
                                    </div>
                                {:else if lookups[field.id]?.type === 'single-select'}
                                    <div class="ml-4 space-y-1">
                                        {#each lookups[field.id].list as option}
                                            <Checkbox
                                                class="card-checkbox"
                                                value={option.id}
                                                checked={projectInput.metadata[field.id]?.id === option.id}
                                                on:change={() => {
                                                    if (projectInput.metadata[field.id]?.id === option.id) {
                                                        projectInput.metadata[field.id] = null;
                                                    } else {
                                                        projectInput.metadata[field.id] = option;
                                                    }
                                                    validateFieldSafe(field.id, projectInput.metadata[field.id]);
                                                }}
                                            >
                                                {option.name}
                                            </Checkbox>
                                        {/each}
                                    </div>
                                {/if}
                                
                            {:else if field.type === 'items'}
                                <div class="ml-4 space-y-2">
                                    {#each field.items || [] as fieldItem}
                                        <div>
                                            <!-- svelte-ignore a11y-label-has-associated-control -->
                                            <label class="card-label-subtext">{fieldItem.label}</label>
                                            <div class="relative">
                                                <MonthYearPicker
                                                    value={projectInput.metadata[field.id]?.[fieldItem.id]}
                                                    openAbove={fieldItem.openAbove}
                                                    error={!!errorState[field.id]}
                                                    on:change={(e) => {
                                                        if (!projectInput.metadata[field.id]) {
                                                            projectInput.metadata[field.id] = {};
                                                        }
                                                        projectInput.metadata[field.id][fieldItem.id] = e.detail;
                                                        validateFieldSafe(field.id, projectInput.metadata[field.id]);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    {/each}
                                </div>                   
 
                            {:else if field.type === 'yearmonthpicker'}
                                <div class="relative">
                                    <MonthYearPicker
                                        value={projectInput.metadata[field.id]}
                                        openAbove={field.openAbove}
                                        error={!!errorState[field.id]}
                                        on:change={(e) => {
                                            projectInput.metadata[field.id] = e.detail;
                                            validateFieldSafe(field.id, projectInput.metadata[field.id]);
                                        }}
                                    />
                                </div>

                            {:else if field.type === 'text'}
                                <Input type="text"
                                    class="app-field {getInputClass(field.id)}"
                                    value={projectInput.metadata[field.id]}
                                    on:input={(e) => {
                                        const value = handleInputValue(e);
                                        projectInput.metadata[field.id] = value;
                                        validateFieldSafe(field.id, value);
                                    }}
                                />

                            {:else if field.type === 'textarea'}
                                <Textarea
                                    class="app-field {getInputClass(field.id)} mt-1"
                                    rows={2}
                                    value={field.format === 'comma-separated' 
                                        ? (Array.isArray(projectInput.metadata[field.id]) 
                                            ? projectInput.metadata[field.id].join(', ') 
                                            : '')
                                        : projectInput.metadata[field.id]}
                                    on:input={(e) => handleTextInput(e, field.id)}
                                    on:blur={(e) => handleTextInput(e, field.id)}
                                    on:keydown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleTextInput(e, field.id);
                                        }
                                    }}
                                />
                            {/if}
                        </div>
                    {/each}
                </div>
            </div>
        </div>
    </div>

    <!-- Action buttons -->
    <div class="flex items-center justify-end mt-4">
        <Button size="xs" color="primary" class="save-button mr-2" on:click={saveChanges} disabled={hasErrors}>
            <FontAwesomeIcon icon={faSave} class="mr-2" />
            Save
        </Button>
        <Button size="xs" color="light" class="cancel-button" on:click={cancelEdit}>
            <FontAwesomeIcon icon={faTimes} class="mr-2" />
            Cancel
        </Button>
    </div>
</Card>
